import React, { useState, useEffect, useRef, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/api-client";
import { keepPreviousSameEntity, invalidatePracticeScoring } from "@/lib/query-keys";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { useToast } from "@/components/ui/use-toast";
import { FileText, Info, Upload, Check, X, Loader2, AlertCircle, Trash2, BarChart3, ChevronDown, ChevronRight, Users, Building2, DollarSign, AlertTriangle, Download } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CmsDataFreshnessCard } from "./CmsDataFreshnessCard";

function csvEscape(v: unknown) {
  const s = v == null ? '' : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function downloadCsv(filename: string, headers: string[], rows: unknown[][]) {
  const lines = [headers.join(','), ...rows.map(r => r.map(csvEscape).join(','))];
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/** special_statuses may be array, object of flags, or string */
function statusLabels(statuses: unknown): string[] {
  if (!statuses) return [];
  if (Array.isArray(statuses)) return statuses.map(String).filter(Boolean);
  if (typeof statuses === 'object') {
    return Object.entries(statuses as Record<string, unknown>)
      .filter(([, v]) => v === true || v === 'Y' || v === 'Yes' || v === 'true' || v === 1)
      .map(([k]) => k.replace(/([A-Z])/g, ' $1').replace(/^./, c => c.toUpperCase()).trim());
  }
  return [String(statuses)];
}

function ListSkeleton() {
  return (
    <div className="space-y-2 py-1">
      <div className="h-14 rounded-lg bg-muted/70 animate-pulse" />
      <div className="h-14 rounded-lg bg-muted/50 animate-pulse" />
    </div>
  );
}

const IA_CATEGORY_MAX_POINTS = 40;

function fmtScoreNum(n: number) {
  return n.toFixed(1).replace(/\.0$/, '');
}

function isSmallPracticeStatus(statuses: unknown): boolean {
  return statusLabels(statuses).some(s => /small\s*practice/i.test(s));
}

/**
 * Score-submission Category Score: Quality/Cost/PI are 0–100.
 * IA is CMS points on a 0–40 scale (full credit = 40 whether small practice = 1 activity
 * or large = 2). Display IA as percent of that max. Category Weight is % of final MIPS.
 */
function formatScoreSubmissionCategory(
  category: 'Quality' | 'Cost' | 'PI' | 'IA',
  score: number | null | undefined,
  weight: number | null | undefined,
  opts?: { isSmallPractice?: boolean },
): { primary: string; secondary: string; muted: boolean } {
  const hasScore = score != null && !Number.isNaN(Number(score));
  const hasWeight = weight != null && !Number.isNaN(Number(weight));
  const w = hasWeight ? Number(weight) : null;
  const raw = hasScore ? Number(score) : null;

  let primary = '—';
  if (raw != null) {
    if (category === 'IA') {
      const pct = Math.min(100, Math.max(0, (raw / IA_CATEGORY_MAX_POINTS) * 100));
      primary = `${fmtScoreNum(pct)} / 100`;
    } else {
      primary = `${fmtScoreNum(raw)} / 100`;
    }
  }

  const parts: string[] = [];
  if (!hasScore && !hasWeight) {
    parts.push('Not in file');
  } else if (w === 0 && hasScore) {
    parts.push('Not applied · weight 0%');
  } else if (w === 0) {
    parts.push('Not scored · weight 0%');
  } else if (hasWeight) {
    parts.push(`Weight ${w}%`);
  }

  if (category === 'IA' && raw != null) {
    parts.push(`${fmtScoreNum(raw)}/${IA_CATEGORY_MAX_POINTS} pts`);
    if (opts?.isSmallPractice) {
      parts.push('1 activity = full credit');
    } else if (opts?.isSmallPractice === false) {
      parts.push('2 activities = full credit');
    }
  }

  return { primary, secondary: parts.join(' · '), muted: w === 0 };
}

interface QPPReportsTabProps {
  practiceId: string;
  programYear: number;
  eligibilityImported: boolean;
  eligibilityImportedAt: string | null;
  onEligibilityImportComplete: (data: any) => void;
  onEligibilityClearImport: () => void;
}

interface ScoreReport {
  program_year: number;   // registry year (filed under)
  data_year?: number;     // the year the score is FOR (label)
  uploaded_at: string;
  overall_score?: number;
  quality_score?: number;
  quality_weight?: number;
  cost_score?: number;
  cost_weight?: number;
  pi_score?: number;
  pi_weight?: number;
  ia_score?: number;
  ia_weight?: number;
  reported_as?: string;
  program_name?: string;
  measures_count?: number;
}

interface ScoreMeasureRow {
  category?: string;
  measure_id?: string;
  measure_name?: string;
  collection_type?: string;
  performance_rate?: string | number;
  decile_score?: number | null;
  data_completeness?: number | null;
  eligible_population?: number | null;
  performance_met?: number | null;
  performance_not_met?: number | null;
  exclusions?: number | null;
  exceptions?: number | null;
}

interface PracticeEligibility {
  program_year: number;
  uploaded_at: string;
  practice_name?: string;
  tin?: string;
  practice_address?: string;
  mips_eligible?: boolean;
  meets_lvt?: boolean;
  opt_in_eligible?: string;
  allowed_charges?: number;
  medicare_patients?: number;
  covered_services?: number;
  special_statuses?: any;
  apm_count?: number;
}

interface ClinicianEligibility {
  program_year?: number;
  npi: string;
  clinician_name: string;
  clinician_type?: string;
  mips_eligible_individual?: boolean;
  mips_eligible_group?: boolean;
  allowed_charges?: number;
  medicare_patients?: number;
  special_statuses?: any;
  qp_status?: string;
  opt_in_eligible?: boolean;
}

interface EligibilityData {
  program_year: number;
  practice?: PracticeEligibility;
  clinicians?: ClinicianEligibility[];
  clinician_count: number;
}

const COST_FILE_TYPE_LABELS: Record<string, string> = {
  episode_performance: 'Episode data',
  supplemental: 'Companion (service categories)',
  tpcc: 'TPCC beneficiaries',
  mspb: 'MSPB episodes',
};

interface CostFeedbackUpload {
  id: number;
  program_year: number;   // registry year (filed under)
  data_year?: number;     // CMS data vintage (label)
  measure_id: string;
  file_type: string;
  upload_section: string;
  file_name: string;
  record_count: number;
  validation_warnings: Array<{ type: string; message: string }>;
  uploaded_at: string;
  uploaded_by_name?: string;
}

interface CostFeedbackData {
  [year: number]: CostFeedbackUpload[];
}

export function QPPReportsTab({
  practiceId,
  programYear,
  eligibilityImported,
  eligibilityImportedAt,
  onEligibilityImportComplete,
  onEligibilityClearImport
}: QPPReportsTabProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
  
  // Eligibility state
  const [eligibilityModalOpen, setEligibilityModalOpen] = useState(false);
  const [eligibilityYear, setEligibilityYear] = useState(programYear.toString());
  const [practicesFile, setPracticesFile] = useState<File | null>(null);
  const [cliniciansFile, setCliniciansFile] = useState<File | null>(null);
  const [eligibilityUploading, setEligibilityUploading] = useState(false);
  const [eligibilityError, setEligibilityError] = useState<string | null>(null);
  const [expandedEligibility, setExpandedEligibility] = useState<number | null>(null);
  const [expandedClinicians, setExpandedClinicians] = useState<number | null>(null);
  const practicesInputRef = useRef<HTMLInputElement>(null);
  const cliniciansInputRef = useRef<HTMLInputElement>(null);
  
  // Score Submission state
  const [scoreModalOpen, setScoreModalOpen] = useState(false);
  const [scoreYear, setScoreYear] = useState((programYear - 1).toString()); // data year (the year the score is FOR)
  const [scoreFile, setScoreFile] = useState<File | null>(null);
  const [scoreUploading, setScoreUploading] = useState(false);
  const [scoreError, setScoreError] = useState<string | null>(null);
  const [expandedScore, setExpandedScore] = useState<number | null>(null);
  const scoreInputRef = useRef<HTMLInputElement>(null);
  
  // Deleting state
  const [deletingPractice, setDeletingPractice] = useState<number | null>(null);
  const [deletingClinicians, setDeletingClinicians] = useState<number | null>(null);
  const [deletingScore, setDeletingScore] = useState<number | null>(null);
  
  // Cost Feedback state
  const [costModalOpen, setCostModalOpen] = useState(false);
  // CMS data year (vintage) + upload section are auto-detected server-side now
  const [costFiles, setCostFiles] = useState<File[]>([]);
  const [costUploading, setCostUploading] = useState(false);
  const [costError, setCostError] = useState<string | null>(null);
  const [costUploadResult, setCostUploadResult] = useState<any>(null);
  const [expandedCost, setExpandedCost] = useState<number | null>(null);
  const [deletingCost, setDeletingCost] = useState<number | null>(null);
  const costInputRef = useRef<HTMLInputElement>(null);

  const eligibilityQ = useQuery<EligibilityData[]>({
    queryKey: ['qpp', 'eligibility-full', practiceId],
    queryFn: () => apiClient.get(`/api/practices/${practiceId}/eligibility-full`),
    enabled: !!practiceId,
    placeholderData: keepPreviousSameEntity(2, String(practiceId ?? '')),
    staleTime: 60_000,
  });
  const scoreReportsQ = useQuery<ScoreReport[]>({
    queryKey: ['qpp', 'score-submissions', practiceId],
    queryFn: () => apiClient.get(`/api/practices/${practiceId}/score-submissions`),
    enabled: !!practiceId,
    placeholderData: keepPreviousSameEntity(2, String(practiceId ?? '')),
    staleTime: 60_000,
  });
  const costFeedbackQ = useQuery<CostFeedbackData>({
    queryKey: ['qpp', 'cost-feedback', practiceId],
    queryFn: () => apiClient.get(`/api/practices/${practiceId}/cost-feedback`),
    enabled: !!practiceId,
    placeholderData: keepPreviousSameEntity(2, String(practiceId ?? '')),
    staleTime: 60_000,
  });
  const scoreDetailQ = useQuery<{ measures?: ScoreMeasureRow[] } & ScoreReport>({
    queryKey: ['qpp', 'score-submission-detail', practiceId, expandedScore ?? ''],
    queryFn: () => apiClient.get(`/api/practices/${practiceId}/score-submissions/${expandedScore}`),
    enabled: !!practiceId && expandedScore != null,
    staleTime: 60_000,
  });

  const eligibilityData = eligibilityQ.data ?? [];
  const scoreReports = scoreReportsQ.data ?? [];
  const costFeedbackData = costFeedbackQ.data ?? {};

  // Each refresh also rings the shared scoring doorbell: these uploads/deletes
  // change what Insights/Overview/MVP display (cost dashboard reads the score
  // file, payment adjustment reads eligibility), and those pages otherwise sit
  // on staleTime with the old numbers (data-freshness review, 2026-08-12).
  const refreshEligibility = () => {
    queryClient.invalidateQueries({ queryKey: ['qpp', 'eligibility-full', practiceId] });
    invalidatePracticeScoring(practiceId);
  };
  const refreshScores = () => {
    queryClient.invalidateQueries({ queryKey: ['qpp', 'score-submissions', practiceId] });
    invalidatePracticeScoring(practiceId);
  };
  const refreshCost = () => {
    queryClient.invalidateQueries({ queryKey: ['qpp', 'cost-feedback', practiceId] });
    invalidatePracticeScoring(practiceId);
  };

  // Drag state for visual feedback (using counters to prevent flicker)
  const [dragCountPractices, setDragCountPractices] = useState(0);
  const [dragCountClinicians, setDragCountClinicians] = useState(0);
  const [dragCountScore, setDragCountScore] = useState(0);
  const [dragCountCost, setDragCountCost] = useState(0);

  // Generate year options
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: currentYear - 2016 }, (_, i) => currentYear - i);

  // Reference data is displayed by the REGISTRY year (program_year = the year the
  // file was filed under) and labeled by its DATA year (the CMS vintage it contains).
  // So a "2024" file filed under 2025 shows under the 2025 view, labeled "2024".
  const eligibilityForYear = useMemo(
    () => eligibilityData.filter(d => d.program_year === programYear),
    [eligibilityData, programYear],
  );
  const scoreReportsForYear = useMemo(
    () => scoreReports.filter(r => r.program_year === programYear),
    [scoreReports, programYear],
  );
  const costFeedbackForYear: CostFeedbackData = useMemo(
    () => Object.fromEntries(
      Object.entries(costFeedbackData).filter(([year]) => parseInt(year, 10) === programYear),
    ),
    [costFeedbackData, programYear],
  );

  // Which cost feedback the engine uses for the viewing year: the file filed under
  // this year if present, else the most recent prior registry year (carry-forward).
  const costRegistryYears = Object.keys(costFeedbackData)
    .map(y => parseInt(y, 10))
    .filter(y => y <= programYear);
  const resolvedRegistryYear = costRegistryYears.length > 0 ? Math.max(...costRegistryYears) : null;
  const resolvedDataYear = resolvedRegistryYear !== null
    ? (costFeedbackData[resolvedRegistryYear]?.[0]?.data_year ?? resolvedRegistryYear - 1)
    : null;
  const costStatusMessage =
    resolvedRegistryYear === null
      ? `No cost feedback available. Upload your latest CMS cost feedback to score the ${programYear} cost category.`
      : resolvedRegistryYear === programYear
        ? `Cost for ${programYear} is using your ${resolvedDataYear} CMS cost feedback (filed under ${programYear}).`
        : `Cost for ${programYear} is carrying forward your ${resolvedDataYear} CMS cost feedback (filed under ${resolvedRegistryYear}). Upload newer data into ${programYear} when it's released.`;

  // Keep the upload-dialog year selects in sync when the viewing year changes
  // (they are initialized from programYear once; without this a year switch
  // left them pointing at the previous year's defaults). The cost dialog no
  // longer has a year select — its data year is auto-detected server-side.
  useEffect(() => {
    setEligibilityYear(programYear.toString());
    setScoreYear((programYear - 1).toString());
  }, [programYear]);

  // Eligibility handlers
  const handleEligibilityFileSelect = (type: 'practices' | 'clinicians') => (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.name.toLowerCase().endsWith('.csv')) {
        setEligibilityError('Please select a CSV file');
        return;
      }
      setEligibilityError(null);
      if (type === 'practices') setPracticesFile(file);
      else setCliniciansFile(file);
    }
  };

  const handleEligibilityDrop = (type: 'practices' | 'clinicians') => (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (type === 'practices') setDragCountPractices(0);
    else setDragCountClinicians(0);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      if (!file.name.toLowerCase().endsWith('.csv')) {
        setEligibilityError('Please select a CSV file');
        return;
      }
      setEligibilityError(null);
      if (type === 'practices') setPracticesFile(file);
      else setCliniciansFile(file);
    }
  };

  const handleEligibilityUpload = async () => {
    if (!practicesFile && !cliniciansFile) {
      setEligibilityError('Please select at least one file');
      return;
    }

    setEligibilityUploading(true);
    setEligibilityError(null);

    try {
      const formData = new FormData();
      formData.append('program_year', eligibilityYear);
      if (practicesFile) formData.append('practices_file', practicesFile);
      if (cliniciansFile) formData.append('clinicians_file', cliniciansFile);

      const response = await fetch(`${apiUrl}/api/practices/${practiceId}/eligibility/import`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
        },
        body: formData
      });

      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Failed to import');

      toast({
        title: "Import Successful",
        description: `Eligibility data for ${eligibilityYear} imported.`,
      });

      onEligibilityImportComplete(result.practice?.mapped_settings);
      setEligibilityModalOpen(false);
      setPracticesFile(null);
      setCliniciansFile(null);
      refreshEligibility();

    } catch (error: any) {
      setEligibilityError(error.message);
    } finally {
      setEligibilityUploading(false);
    }
  };

  const handleDeletePracticeEligibility = async (year: number) => {
    setDeletingPractice(year);
    try {
      const response = await fetch(`${apiUrl}/api/practices/${practiceId}/eligibility/${year}/practice`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` },
      });
      if (!response.ok) throw new Error('Failed to delete');
      
      toast({ title: "Deleted", description: `Practice eligibility for ${year} removed.` });
      if (year === programYear) onEligibilityClearImport();
      refreshEligibility();
    } catch (error: any) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } finally {
      setDeletingPractice(null);
    }
  };

  const handleDeleteCliniciansEligibility = async (year: number) => {
    setDeletingClinicians(year);
    try {
      const response = await fetch(`${apiUrl}/api/practices/${practiceId}/eligibility/${year}/clinicians`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` },
      });
      if (!response.ok) throw new Error('Failed to delete');
      
      toast({ title: "Deleted", description: `Clinician eligibility for ${year} removed.` });
      refreshEligibility();
    } catch (error: any) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } finally {
      setDeletingClinicians(null);
    }
  };

  // Score handlers
  const handleScoreFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.name.toLowerCase().endsWith('.csv')) {
        setScoreError('Please select a CSV file');
        return;
      }
      setScoreError(null);
      setScoreFile(file);
    }
  };

  const handleScoreDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragCountScore(0);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      if (!file.name.toLowerCase().endsWith('.csv')) {
        setScoreError('Please select a CSV file');
        return;
      }
      setScoreError(null);
      setScoreFile(file);
    }
  };

  const handleScoreUpload = async () => {
    if (!scoreFile) {
      setScoreError('Please select a file');
      return;
    }

    setScoreUploading(true);
    setScoreError(null);

    try {
      const formData = new FormData();
      formData.append('file', scoreFile);
      formData.append('program_year', programYear.toString()); // registry = the year being viewed
      formData.append('data_year', scoreYear);                  // the year the score is for

      const response = await fetch(`${apiUrl}/api/practices/${practiceId}/score-submissions/import`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
        },
        body: formData
      });

      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Failed to import');

      toast({
        title: "Import Successful",
        description: `Score submission for ${scoreYear} imported. Overall: ${result.data.overall_score}`,
      });

      setScoreModalOpen(false);
      setScoreFile(null);
      refreshScores();
      queryClient.invalidateQueries({ queryKey: ['qpp', 'score-submission-detail', practiceId] });
      queryClient.invalidateQueries({ queryKey: ['mvp', 'score-submission', practiceId] });

    } catch (error: any) {
      setScoreError(error.message);
    } finally {
      setScoreUploading(false);
    }
  };

  const handleDeleteScore = async (year: number) => {
    setDeletingScore(year);
    try {
      const response = await fetch(`${apiUrl}/api/practices/${practiceId}/score-submissions/${year}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` },
      });
      if (!response.ok) throw new Error('Failed to delete');
      
      toast({ title: "Deleted", description: `Score submission for ${year} removed.` });
      refreshScores();
      queryClient.invalidateQueries({ queryKey: ['qpp', 'score-submission-detail', practiceId] });
      queryClient.invalidateQueries({ queryKey: ['mvp', 'score-submission', practiceId] });
    } catch (error: any) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } finally {
      setDeletingScore(null);
    }
  };

  const isCostUploadable = (f: File) => /\.(csv|zip)$/i.test(f.name);

  const handleCostFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const accepted = files.filter(isCostUploadable);
    
    if (accepted.length !== files.length) {
      setCostError('Only CSV or ZIP files are allowed');
      return;
    }
    
    setCostError(null);
    setCostFiles(prev => [...prev, ...accepted]);
  };

  const handleCostDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragCountCost(0);
    const files = Array.from(e.dataTransfer.files);
    const accepted = files.filter(isCostUploadable);
    
    if (accepted.length !== files.length) {
      setCostError('Only CSV or ZIP files are allowed');
      return;
    }
    
    setCostError(null);
    setCostFiles(prev => [...prev, ...accepted]);
  };

  const removeCostFile = (index: number) => {
    setCostFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleCostUpload = async () => {
    if (costFiles.length === 0) {
      setCostError('Please select at least one file');
      return;
    }

    setCostUploading(true);
    setCostError(null);
    setCostUploadResult(null);

    try {
      const formData = new FormData();
      formData.append('program_year', programYear.toString()); // registry = the year being viewed
      // data_year (CMS vintage) and upload section are auto-detected server-side
      costFiles.forEach(file => formData.append('files', file));

      const response = await fetch(`${apiUrl}/api/practices/${practiceId}/cost-feedback/import`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
        },
        body: formData
      });

      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Failed to import');

      setCostUploadResult(result.results);
      
      if (result.results.success.length > 0) {
        toast({
          title: "Import Successful",
          description: `Imported ${result.results.success.length} cost feedback file(s).`,
        });
        refreshCost();
      }
      
      if (result.results.errors.length === 0 && result.results.warnings.length === 0) {
        setCostModalOpen(false);
        setCostFiles([]);
        setCostUploadResult(null);
      }

    } catch (error: any) {
      setCostError(error.message);
    } finally {
      setCostUploading(false);
    }
  };

  const handleDeleteCostFeedback = async (uploadId: number, measureId: string) => {
    setDeletingCost(uploadId);
    try {
      const response = await fetch(`${apiUrl}/api/practices/${practiceId}/cost-feedback/${uploadId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
        }
      });
      if (!response.ok) throw new Error('Failed to delete');
      
      toast({ title: "Deleted", description: `Cost feedback for ${measureId} removed.` });
      refreshCost();
    } catch (error: any) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } finally {
      setDeletingCost(null);
    }
  };

  const downloadPracticeEligibilityCsv = (data: EligibilityData) => {
    const p = data.practice;
    if (!p) return;
    downloadCsv(
      `eligibility-practice-${practiceId}-py${data.program_year}.csv`,
      ['program_year', 'practice_name', 'tin', 'address', 'mips_eligible', 'meets_lvt', 'opt_in_eligible',
        'allowed_charges', 'medicare_patients', 'covered_services', 'apm_count', 'special_statuses', 'uploaded_at'],
      [[
        data.program_year, p.practice_name, p.tin, p.practice_address, p.mips_eligible, p.meets_lvt,
        p.opt_in_eligible, p.allowed_charges, p.medicare_patients, p.covered_services, p.apm_count,
        statusLabels(p.special_statuses).join('; '), p.uploaded_at,
      ]],
    );
  };

  const downloadClinicianEligibilityCsv = (data: EligibilityData) => {
    downloadCsv(
      `eligibility-clinicians-${practiceId}-py${data.program_year}.csv`,
      ['npi', 'clinician_name', 'clinician_type', 'mips_eligible_individual', 'mips_eligible_group',
        'allowed_charges', 'medicare_patients', 'qp_status', 'opt_in_eligible', 'special_statuses'],
      (data.clinicians ?? []).map(c => [
        c.npi, c.clinician_name, c.clinician_type, c.mips_eligible_individual, c.mips_eligible_group,
        c.allowed_charges, c.medicare_patients, c.qp_status, c.opt_in_eligible,
        statusLabels(c.special_statuses).join('; '),
      ]),
    );
  };

  const downloadScoreSubmissionCsv = (report: ScoreReport, measures: ScoreMeasureRow[]) => {
    downloadCsv(
      `score-submission-${practiceId}-py${report.program_year}.csv`,
      ['category', 'measure_id', 'measure_name', 'collection_type', 'performance_rate', 'decile_score',
        'data_completeness', 'eligible_population', 'performance_met', 'performance_not_met',
        'exclusions', 'exceptions'],
      measures.map(m => [
        m.category, m.measure_id, m.measure_name, m.collection_type, m.performance_rate, m.decile_score,
        m.data_completeness, m.eligible_population, m.performance_met, m.performance_not_met,
        m.exclusions, m.exceptions,
      ]),
    );
  };

  const getMeasureLabel = (measureId: string) => {
    const labels: Record<string, string> = {
      'TPCC_1': 'Total Per Capita Cost',
      'MSPB_1': 'Medicare Spending Per Beneficiary',
      'COST_D_1': 'Diabetes',
      'COST_HF_1': 'Heart Failure',
      'COST_ACOPD_1': 'Asthma/COPD',
      'COST_COPDE_1': 'COPD Exacerbation',
      'COST_IHCI_1': 'Intracranial Hemorrhage/Cerebral Infarction',
      'COST_LGH_1': 'Lower GI Hemorrhage',
      'COST_S_1': 'Sepsis',
      'COST_STEMI_1': 'STEMI with PCI',
      'COST_PRC_1': 'Psychoses and Related Conditions',
      'COST_DEP_1': 'Depression',
      'COST_LBP_1': 'Low Back Pain',
      'COST_EDV_1': 'Emergency Medicine',
      'COST_SSC_1': 'Screening/Surveillance Colonoscopy',
      'COST_LSFDD_1': 'Lumbar Spine Fusion',
    };
    return labels[measureId] || measureId;
  };

  const getFileTypeLabel = (fileType: string) => {
    const labels: Record<string, string> = {
      'episode_performance': 'Episode Performance',
      'supplemental': 'Supplemental',
      'tpcc': 'TPCC',
      'mspb': 'MSPB',
    };
    return labels[fileType] || fileType;
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnterPractices = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragCountPractices(prev => prev + 1);
  };

  const handleDragLeavePractices = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragCountPractices(prev => prev - 1);
  };

  const handleDragEnterClinicians = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragCountClinicians(prev => prev + 1);
  };

  const handleDragLeaveClinicians = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragCountClinicians(prev => prev - 1);
  };

  const handleDragEnterScore = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragCountScore(prev => prev + 1);
  };

  const handleDragLeaveScore = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragCountScore(prev => prev - 1);
  };

  const handleDragEnterCost = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragCountCost(prev => prev + 1);
  };

  const handleDragLeaveCost = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragCountCost(prev => prev - 1);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric'
    });
  };

  const formatCurrency = (value: number | undefined) => {
    if (!value) return 'N/A';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);
  };

  return (
    <div className="space-y-6">
      {/* Intro Card */}
      <Card className="border-blue-200 bg-blue-50/50">
        <CardContent className="py-4">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-blue-900">Upload your CMS QPP Portal reports</p>
              <p className="text-sm text-blue-700 mt-1">
                Download your eligibility and performance reports from the{" "}
                <a href="https://qpp.cms.gov" target="_blank" rel="noopener noreferrer" className="underline hover:text-blue-900">
                  CMS QPP Portal
                </a>
                {" "}and upload them here.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Medicare charges from CMS (MVP C2 — hydration's home on QPP Reports) */}
      <CmsDataFreshnessCard practiceId={practiceId} programYear={programYear} />

      {/* Eligibility Files Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Eligibility Files
              </CardTitle>
              <CardDescription>Practice and clinician eligibility data</CardDescription>
            </div>
            <Button onClick={() => setEligibilityModalOpen(true)}>
              <Upload className="w-4 h-4 mr-2" />
              Upload
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {!eligibilityQ.isFetched ? (
            <ListSkeleton />
          ) : eligibilityForYear.length === 0 ? (
            <div className="text-center py-6 text-muted-foreground">
              <FileText className="w-8 h-8 mx-auto mb-2 opacity-40" />
              <p className="text-sm">No eligibility files uploaded for {programYear}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {eligibilityForYear.map((data) => (
                <div key={data.program_year} className="space-y-2">
                  {data.practice && (
                    <Collapsible
                      open={expandedEligibility === data.program_year}
                      onOpenChange={(open) => setExpandedEligibility(open ? data.program_year : null)}
                    >
                      <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border">
                        <CollapsibleTrigger className="flex items-center gap-3 flex-1 text-left">
                          {expandedEligibility === data.program_year ? (
                            <ChevronDown className="w-4 h-4 text-slate-500" />
                          ) : (
                            <ChevronRight className="w-4 h-4 text-slate-500" />
                          )}
                          <Building2 className="w-5 h-5 text-blue-600" />
                          <div>
                            <p className="text-sm font-medium">{data.program_year} Practice Eligibility</p>
                            <p className="text-xs text-muted-foreground">
                              {data.practice.practice_name || 'Practice'} • Imported {formatDate(data.practice.uploaded_at)}
                            </p>
                          </div>
                        </CollapsibleTrigger>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeletePracticeEligibility(data.program_year)}
                          disabled={deletingPractice === data.program_year}
                          className="text-slate-500 hover:text-red-600"
                        >
                          {deletingPractice === data.program_year ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                        </Button>
                      </div>
                      <CollapsibleContent className="mt-2 ml-2 sm:ml-8 rounded-lg border bg-white p-3 space-y-3">
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Practice summary</p>
                          <Button size="sm" variant="outline" className="h-8" onClick={() => downloadPracticeEligibilityCsv(data)}>
                            <Download className="w-3.5 h-3.5 mr-1.5" />
                            Download CSV
                          </Button>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-2 text-sm">
                          <div><span className="text-muted-foreground block text-xs">TIN</span>{data.practice.tin || '—'}</div>
                          <div><span className="text-muted-foreground block text-xs">MIPS eligible</span>{data.practice.mips_eligible ? 'Yes' : 'No'}</div>
                          <div><span className="text-muted-foreground block text-xs">Allowed charges</span>{formatCurrency(data.practice.allowed_charges)}</div>
                          <div><span className="text-muted-foreground block text-xs">Medicare patients</span>{data.practice.medicare_patients ?? '—'}</div>
                          <div><span className="text-muted-foreground block text-xs">Covered services</span>{data.practice.covered_services ?? '—'}</div>
                          <div><span className="text-muted-foreground block text-xs">Meets LVT</span>{data.practice.meets_lvt == null ? '—' : (data.practice.meets_lvt ? 'Yes' : 'No')}</div>
                          <div><span className="text-muted-foreground block text-xs">Opt-in eligible</span>{data.practice.opt_in_eligible ?? '—'}</div>
                          <div><span className="text-muted-foreground block text-xs">APM count</span>{data.practice.apm_count ?? '—'}</div>
                          {data.practice.practice_address && (
                            <div className="col-span-2 md:col-span-3">
                              <span className="text-muted-foreground block text-xs">Address</span>
                              {data.practice.practice_address}
                            </div>
                          )}
                        </div>
                        {statusLabels(data.practice.special_statuses).length > 0 && (
                          <div className="flex flex-wrap gap-1.5">
                            {statusLabels(data.practice.special_statuses).map(s => (
                              <Badge key={s} variant="secondary" className="font-normal text-xs">{s}</Badge>
                            ))}
                          </div>
                        )}
                      </CollapsibleContent>
                    </Collapsible>
                  )}

                  {data.clinician_count > 0 && (
                    <Collapsible
                      open={expandedClinicians === data.program_year}
                      onOpenChange={(open) => setExpandedClinicians(open ? data.program_year : null)}
                    >
                      <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border">
                        <CollapsibleTrigger className="flex items-center gap-3 flex-1 text-left">
                          {expandedClinicians === data.program_year ? (
                            <ChevronDown className="w-4 h-4 text-slate-500" />
                          ) : (
                            <ChevronRight className="w-4 h-4 text-slate-500" />
                          )}
                          <Users className="w-5 h-5 text-green-600" />
                          <div>
                            <p className="text-sm font-medium">{data.program_year} Clinician Eligibility</p>
                            <p className="text-xs text-muted-foreground">
                              {data.clinician_count} clinician{data.clinician_count !== 1 ? 's' : ''} imported
                            </p>
                          </div>
                        </CollapsibleTrigger>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteCliniciansEligibility(data.program_year)}
                          disabled={deletingClinicians === data.program_year}
                          className="text-slate-500 hover:text-red-600"
                        >
                          {deletingClinicians === data.program_year ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                        </Button>
                      </div>
                      <CollapsibleContent className="mt-2 ml-2 sm:ml-8 rounded-lg border bg-white overflow-hidden">
                        <div className="flex items-center justify-between gap-2 px-3 py-2 border-b bg-slate-50/80">
                          <p className="text-xs font-medium text-muted-foreground">
                            {data.clinician_count} clinicians
                          </p>
                          <Button size="sm" variant="outline" className="h-8" onClick={() => downloadClinicianEligibilityCsv(data)}>
                            <Download className="w-3.5 h-3.5 mr-1.5" />
                            Download CSV
                          </Button>
                        </div>
                        <div className="max-h-72 overflow-auto">
                          <table className="w-full text-xs">
                            <thead className="sticky top-0 bg-slate-50 text-muted-foreground">
                              <tr className="border-b text-left">
                                <th className="px-3 py-2 font-medium">Clinician</th>
                                <th className="px-3 py-2 font-medium">NPI</th>
                                <th className="px-3 py-2 font-medium">Type</th>
                                <th className="px-3 py-2 font-medium text-center">Indiv</th>
                                <th className="px-3 py-2 font-medium text-center">Group</th>
                                <th className="px-3 py-2 font-medium text-right">Charges</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(data.clinicians ?? []).map((c) => (
                                <tr key={c.npi} className="border-b last:border-0 hover:bg-slate-50/80">
                                  <td className="px-3 py-2 font-medium text-foreground">{c.clinician_name || '—'}</td>
                                  <td className="px-3 py-2 font-mono text-muted-foreground">{c.npi}</td>
                                  <td className="px-3 py-2 text-muted-foreground">{c.clinician_type || '—'}</td>
                                  <td className="px-3 py-2 text-center">{c.mips_eligible_individual ? 'Yes' : 'No'}</td>
                                  <td className="px-3 py-2 text-center">{c.mips_eligible_group ? 'Yes' : 'No'}</td>
                                  <td className="px-3 py-2 text-right tabular-nums">{formatCurrency(c.allowed_charges)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </CollapsibleContent>
                    </Collapsible>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Score Submissions Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5" />
                Score Submissions
              </CardTitle>
              <CardDescription>Historical MIPS performance data</CardDescription>
            </div>
            <Button onClick={() => setScoreModalOpen(true)}>
              <Upload className="w-4 h-4 mr-2" />
              Upload
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {!scoreReportsQ.isFetched ? (
            <ListSkeleton />
          ) : scoreReportsForYear.length === 0 ? (
            <div className="text-center py-6 text-muted-foreground">
              <BarChart3 className="w-8 h-8 mx-auto mb-2 opacity-40" />
              <p className="text-sm">No {programYear} score submission uploaded</p>
            </div>
          ) : (
            <div className="space-y-2">
              {scoreReportsForYear.map((report) => {
                const isOpen = expandedScore === report.program_year;
                const measures = (isOpen ? scoreDetailQ.data?.measures : null) ?? [];
                return (
                  <Collapsible
                    key={report.program_year}
                    open={isOpen}
                    onOpenChange={(open) => setExpandedScore(open ? report.program_year : null)}
                  >
                    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border">
                      <CollapsibleTrigger className="flex items-center gap-3 flex-1 text-left">
                        {isOpen ? (
                          <ChevronDown className="w-4 h-4 text-slate-500" />
                        ) : (
                          <ChevronRight className="w-4 h-4 text-slate-500" />
                        )}
                        <BarChart3 className="w-5 h-5 text-purple-600" />
                        <div>
                          <p className="text-sm font-medium">{report.data_year ?? report.program_year} Score Submission</p>
                          <p className="text-xs text-muted-foreground">
                            Overall: {report.overall_score ?? 'N/A'}
                            {report.measures_count != null && <> · {report.measures_count} measures</>}
                            {' · '}Filed under {report.program_year} · Imported {formatDate(report.uploaded_at)}
                          </p>
                        </div>
                      </CollapsibleTrigger>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteScore(report.program_year)}
                        disabled={deletingScore === report.program_year}
                        className="text-slate-500 hover:text-red-600"
                      >
                        {deletingScore === report.program_year ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                    <CollapsibleContent className="mt-2 ml-2 sm:ml-8 rounded-lg border bg-white overflow-hidden">
                      <div className="p-3 space-y-3 border-b">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <div className="text-xs text-muted-foreground">
                            {report.program_name && <span className="mr-2">{report.program_name}</span>}
                            {report.reported_as && <span>Reported as {report.reported_as}</span>}
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-8"
                            disabled={!measures.length}
                            onClick={() => downloadScoreSubmissionCsv(report, measures)}
                          >
                            <Download className="w-3.5 h-3.5 mr-1.5" />
                            Download CSV
                          </Button>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                          {(() => {
                            const elig = eligibilityData.find(d => d.program_year === report.program_year)
                              ?? eligibilityForYear[0];
                            const smallKnown = elig?.practice
                              ? isSmallPracticeStatus(elig.practice.special_statuses)
                              : undefined;
                            return ([
                              ['Quality', report.quality_score, report.quality_weight],
                              ['Cost', report.cost_score, report.cost_weight],
                              ['PI', report.pi_score, report.pi_weight],
                              ['IA', report.ia_score, report.ia_weight],
                            ] as const).map(([label, score, weight]) => {
                              const { primary, secondary, muted } = formatScoreSubmissionCategory(
                                label,
                                score,
                                weight,
                                label === 'IA' ? { isSmallPractice: smallKnown } : undefined,
                              );
                              return (
                                <div
                                  key={label}
                                  className={`rounded-md border px-3 py-2 ${
                                    muted ? 'bg-slate-50/50 border-slate-200/80 opacity-75' : 'bg-slate-50/80'
                                  }`}
                                >
                                  <div className="text-xs text-muted-foreground">{label}</div>
                                  <div className="font-semibold tabular-nums">{primary}</div>
                                  {secondary && (
                                    <div className="text-xs text-muted-foreground mt-0.5 leading-snug">{secondary}</div>
                                  )}
                                </div>
                              );
                            });
                          })()}
                        </div>
                      </div>
                      {isOpen && scoreDetailQ.isFetching && !scoreDetailQ.data ? (
                        <div className="p-6 flex justify-center">
                          <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                        </div>
                      ) : (
                        <div className="max-h-80 overflow-auto">
                          <table className="w-full text-xs">
                            <thead className="sticky top-0 bg-slate-50 text-muted-foreground">
                              <tr className="border-b text-left">
                                <th className="px-3 py-2 font-medium">Category</th>
                                <th className="px-3 py-2 font-medium">ID</th>
                                <th className="px-3 py-2 font-medium">Measure</th>
                                <th className="px-3 py-2 font-medium">Collection</th>
                                <th className="px-3 py-2 font-medium text-right">Rate</th>
                                <th className="px-3 py-2 font-medium text-right">Decile</th>
                              </tr>
                            </thead>
                            <tbody>
                              {measures.map((m, i) => (
                                <tr key={`${m.measure_id}-${i}`} className="border-b last:border-0 hover:bg-slate-50/80">
                                  <td className="px-3 py-2 capitalize text-muted-foreground">{m.category || '—'}</td>
                                  <td className="px-3 py-2 font-mono">{m.measure_id || '—'}</td>
                                  <td className="px-3 py-2 max-w-[14rem] truncate" title={m.measure_name}>{m.measure_name || '—'}</td>
                                  <td className="px-3 py-2 text-muted-foreground">{m.collection_type || '—'}</td>
                                  <td className="px-3 py-2 text-right tabular-nums">{m.performance_rate ?? '—'}</td>
                                  <td className="px-3 py-2 text-right tabular-nums">{m.decile_score ?? '—'}</td>
                                </tr>
                              ))}
                              {measures.length === 0 && !scoreDetailQ.isFetching && (
                                <tr>
                                  <td colSpan={6} className="px-3 py-6 text-center text-muted-foreground">
                                    No measure rows in this submission.
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </CollapsibleContent>
                  </Collapsible>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Cost Feedback Reports Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="w-5 h-5" />
                Cost Feedback Reports
              </CardTitle>
              <CardDescription>Historical cost measure performance data</CardDescription>
            </div>
            <Button onClick={() => setCostModalOpen(true)}>
              <Upload className="w-4 h-4 mr-2" />
              Upload
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className={`mb-3 rounded-md border px-3 py-2 text-sm flex items-start gap-2 ${resolvedRegistryYear === null ? 'border-amber-300 bg-amber-50 text-amber-800' : 'border-blue-200 bg-blue-50 text-blue-800'}`}>
            <Info className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span>{costStatusMessage}</span>
          </div>
          {!costFeedbackQ.isFetched ? (
            <ListSkeleton />
          ) : Object.keys(costFeedbackForYear).length === 0 ? (
            <div className="text-center py-6 text-muted-foreground">
              <DollarSign className="w-8 h-8 mx-auto mb-2 opacity-40" />
              <p className="text-sm">No cost feedback uploaded to the {programYear} registry</p>
            </div>
          ) : (
            <div className="space-y-3">
              {Object.entries(costFeedbackForYear)
                .sort(([a], [b]) => Number(b) - Number(a))
                .map(([year, uploads]) => (
                  <Collapsible
                    key={year}
                    open={expandedCost === Number(year)}
                    onOpenChange={(open) => setExpandedCost(open ? Number(year) : null)}
                  >
                    <CollapsibleTrigger className="w-full">
                      <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border hover:bg-slate-100 transition-colors">
                        <div className="flex items-center gap-3">
                          {expandedCost === Number(year) ? (
                            <ChevronDown className="w-4 h-4 text-slate-500" />
                          ) : (
                            <ChevronRight className="w-4 h-4 text-slate-500" />
                          )}
                          <DollarSign className="w-5 h-5 text-emerald-600" />
                          <div className="text-left">
                            <p className="text-sm font-medium">{uploads[0]?.data_year ?? Number(year) - 1} Cost Feedback</p>
                            <p className="text-xs text-muted-foreground">
                              {uploads.length} measure{uploads.length !== 1 ? 's' : ''} • filed under {year}
                            </p>
                          </div>
                        </div>
                      </div>
                    </CollapsibleTrigger>
                    <CollapsibleContent className="mt-2 space-y-2">
                      {uploads.map((upload) => (
                        <div key={upload.id} className="ml-8 p-3 bg-slate-50/50 rounded-lg border">
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <p className="text-sm font-medium">{upload.measure_id}</p>
                                <Badge variant="outline" className="text-xs">
                                  {getFileTypeLabel(upload.file_type)}
                                </Badge>
                              </div>
                              <p className="text-xs text-muted-foreground mt-1">
                                {getMeasureLabel(upload.measure_id)} • {upload.record_count} records
                              </p>
                              <p className="text-xs text-muted-foreground">
                                Imported {formatDate(upload.uploaded_at)}
                                {upload.uploaded_by_name && ` by ${upload.uploaded_by_name}`}
                              </p>
                              {upload.validation_warnings && upload.validation_warnings.length > 0 && (
                                <div className="mt-2 flex items-start gap-1.5 text-amber-600">
                                  <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                                  <div className="text-xs">
                                    {upload.validation_warnings.map((w, i) => (
                                      <p key={i}>{w.message}</p>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteCostFeedback(upload.id, upload.measure_id)}
                              disabled={deletingCost === upload.id}
                              className="text-slate-500 hover:text-red-600"
                            >
                              {deletingCost === upload.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Trash2 className="w-4 h-4" />
                              )}
                            </Button>
                          </div>
                        </div>
                      ))}
                    </CollapsibleContent>
                  </Collapsible>
                ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Eligibility Upload Modal */}
      <Dialog open={eligibilityModalOpen} onOpenChange={setEligibilityModalOpen} modal={false}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Upload Eligibility Files</DialogTitle>
            <DialogDescription>Upload practice and/or clinician eligibility files</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Performance Year <span className="text-red-500">*</span></label>
              <Select value={eligibilityYear} onValueChange={setEligibilityYear}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {yearOptions.map((y) => <SelectItem key={y} value={y.toString()}>{y}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Practice Eligibility File</label>
              <div
                className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${
                  practicesFile 
                    ? 'border-green-300 bg-green-50' 
                    : dragCountPractices > 0 
                      ? 'border-primary bg-primary/10' 
                      : 'border-gray-200 hover:border-primary/50'
                }`}
                onClick={() => practicesInputRef.current?.click()}
                onDrop={handleEligibilityDrop('practices')}
                onDragOver={handleDragOver}
                onDragEnter={handleDragEnterPractices}
                onDragLeave={handleDragLeavePractices}
              >
                <input ref={practicesInputRef} type="file" accept=".csv" className="hidden" onChange={handleEligibilityFileSelect('practices')} />
                {practicesFile ? (
                  <div className="flex items-center justify-center gap-2 text-green-700">
                    <Check className="w-5 h-5" />
                    <span className="text-sm font-medium">{practicesFile.name}</span>
                    <button onClick={(e) => { e.stopPropagation(); setPracticesFile(null); }} className="ml-2 text-gray-400 hover:text-red-500">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div>
                    <Upload className="w-6 h-6 text-gray-400 mx-auto mb-1" />
                    <p className="text-sm text-gray-600">Drop file or click to browse</p>
                  </div>
                )}
              </div>
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Clinician Eligibility File</label>
              <div
                className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${
                  cliniciansFile 
                    ? 'border-green-300 bg-green-50' 
                    : dragCountClinicians > 0 
                      ? 'border-primary bg-primary/10' 
                      : 'border-gray-200 hover:border-primary/50'
                }`}
                onClick={() => cliniciansInputRef.current?.click()}
                onDrop={handleEligibilityDrop('clinicians')}
                onDragOver={handleDragOver}
                onDragEnter={handleDragEnterClinicians}
                onDragLeave={handleDragLeaveClinicians}
              >
                <input ref={cliniciansInputRef} type="file" accept=".csv" className="hidden" onChange={handleEligibilityFileSelect('clinicians')} />
                {cliniciansFile ? (
                  <div className="flex items-center justify-center gap-2 text-green-700">
                    <Check className="w-5 h-5" />
                    <span className="text-sm font-medium">{cliniciansFile.name}</span>
                    <button onClick={(e) => { e.stopPropagation(); setCliniciansFile(null); }} className="ml-2 text-gray-400 hover:text-red-500">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div>
                    <Upload className="w-6 h-6 text-gray-400 mx-auto mb-1" />
                    <p className="text-sm text-gray-600">Drop file or click to browse</p>
                  </div>
                )}
              </div>
            </div>

            {eligibilityError && (
              <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700">
                <AlertCircle className="w-4 h-4" />
                <span className="text-sm">{eligibilityError}</span>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEligibilityModalOpen(false)} disabled={eligibilityUploading}>Cancel</Button>
            <Button onClick={handleEligibilityUpload} disabled={(!practicesFile && !cliniciansFile) || eligibilityUploading}>
              {eligibilityUploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Importing...</> : <><Upload className="w-4 h-4 mr-2" />Import</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Score Upload Modal */}
      <Dialog open={scoreModalOpen} onOpenChange={setScoreModalOpen} modal={false}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Upload Score Submission</DialogTitle>
            <DialogDescription>Upload your score submission CSV from the QPP portal</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Score year (the year this score is for) <span className="text-red-500">*</span></label>
              <Select value={scoreYear} onValueChange={setScoreYear}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {yearOptions.map((y) => <SelectItem key={y} value={y.toString()}>{y}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">Filed under the year you're viewing ({programYear}).</p>
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Score Submission File <span className="text-red-500">*</span></label>
              <div
                className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${
                  scoreFile 
                    ? 'border-green-300 bg-green-50' 
                    : dragCountScore > 0 
                      ? 'border-primary bg-primary/10' 
                      : 'border-gray-200 hover:border-primary/50'
                }`}
                onClick={() => scoreInputRef.current?.click()}
                onDrop={handleScoreDrop}
                onDragOver={handleDragOver}
                onDragEnter={handleDragEnterScore}
                onDragLeave={handleDragLeaveScore}
              >
                <input ref={scoreInputRef} type="file" accept=".csv" className="hidden" onChange={handleScoreFileSelect} />
                {scoreFile ? (
                  <div className="flex items-center justify-center gap-2 text-green-700">
                    <Check className="w-5 h-5" />
                    <span className="text-sm font-medium">{scoreFile.name}</span>
                    <button onClick={(e) => { e.stopPropagation(); setScoreFile(null); }} className="ml-2 text-gray-400 hover:text-red-500">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div>
                    <Upload className="w-6 h-6 text-gray-400 mx-auto mb-1" />
                    <p className="text-sm text-gray-600">Drop file or click to browse</p>
                  </div>
                )}
              </div>
            </div>

            {scoreError && (
              <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700">
                <AlertCircle className="w-4 h-4" />
                <span className="text-sm">{scoreError}</span>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setScoreModalOpen(false)} disabled={scoreUploading}>Cancel</Button>
            <Button onClick={handleScoreUpload} disabled={!scoreFile || scoreUploading}>
              {scoreUploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Importing...</> : <><Upload className="w-4 h-4 mr-2" />Import</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cost Feedback Upload Modal */}
      <Dialog open={costModalOpen} modal={false} onOpenChange={(open) => {
        setCostModalOpen(open);
        if (!open) {
          setCostFiles([]);
          setCostError(null);
          setCostUploadResult(null);
        }
      }}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>Upload Cost Feedback Reports</DialogTitle>
            <DialogDescription>
              Drop the zip files exactly as downloaded from the QPP portal — no unzipping needed
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="text-xs text-muted-foreground p-2 bg-slate-50 rounded">
              Files are identified automatically (episode data, companion/service-category
              reports, TPCC, MSPB — including the CMS data year, read from the service dates).
              Everything is filed under <span className="font-medium">{programYear}</span>,
              the year you're viewing.
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Cost Feedback Files <span className="text-red-500">*</span></label>
              <div
                className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${
                  costFiles.length > 0 
                    ? 'border-green-300 bg-green-50' 
                    : dragCountCost > 0 
                      ? 'border-primary bg-primary/10' 
                      : 'border-gray-200 hover:border-primary/50'
                }`}
                onClick={() => costInputRef.current?.click()}
                onDrop={handleCostDrop}
                onDragOver={handleDragOver}
                onDragEnter={handleDragEnterCost}
                onDragLeave={handleDragLeaveCost}
              >
                <input 
                  ref={costInputRef} 
                  type="file" 
                  accept=".csv,.zip" 
                  multiple 
                  className="hidden" 
                  onChange={handleCostFileSelect} 
                />
                <Upload className="w-6 h-6 text-gray-400 mx-auto mb-1" />
                <p className="text-sm text-gray-600">Drop files or click to browse</p>
                <p className="text-xs text-gray-400 mt-1">Select multiple ZIP or CSV files — QPP downloads work as-is</p>
              </div>
            </div>

            {costFiles.length > 0 && (
              <div className="space-y-2 max-h-32 overflow-y-auto">
                {costFiles.map((file, index) => (
                  <div key={index} className="flex items-center justify-between p-2 bg-green-50 border border-green-200 rounded text-sm">
                    <div className="flex items-center gap-2 text-green-700">
                      <FileText className="w-4 h-4" />
                      <span className="truncate max-w-[280px]">{file.name}</span>
                    </div>
                    <button 
                      onClick={() => removeCostFile(index)} 
                      className="text-gray-400 hover:text-red-500"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {costError && (
              <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700">
                <AlertCircle className="w-4 h-4" />
                <span className="text-sm">{costError}</span>
              </div>
            )}

            {costUploadResult && (
              <div className="space-y-2">
                {costUploadResult.success.length > 0 && (
                  <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-sm font-medium text-green-700 flex items-center gap-2">
                      <Check className="w-4 h-4" />
                      Successfully imported:
                    </p>
                    <ul className="mt-1 text-xs text-green-600 ml-6 list-disc">
                      {costUploadResult.success.map((s: any, i: number) => (
                        <li key={i}>
                          {s.measureId} — {COST_FILE_TYPE_LABELS[s.fileType] ?? s.fileType} · {s.recordCount} records
                          {s.dataYear ? ` · CMS ${s.dataYear} data` : ''}
                          {s.replaced ? ' · replaced previous upload' : ''}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {costUploadResult.errors.length > 0 && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm font-medium text-red-700 flex items-center gap-2">
                      <AlertCircle className="w-4 h-4" />
                      Errors:
                    </p>
                    <ul className="mt-1 text-xs text-red-600 ml-6 list-disc">
                      {costUploadResult.errors.map((e: any, i: number) => (
                        <li key={i}>{e.file}: {e.error}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {costUploadResult.warnings.length > 0 && (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-sm font-medium text-amber-700 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" />
                      Warnings:
                    </p>
                    <ul className="mt-1 text-xs text-amber-600 ml-6 list-disc">
                      {costUploadResult.warnings.map((w: any, i: number) => (
                        <li key={i}>{w.measureId}: {w.warnings.map((x: any) => x.message).join('; ')}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setCostModalOpen(false);
                setCostFiles([]);
                setCostError(null);
                setCostUploadResult(null);
              }} 
              disabled={costUploading}
            >
              {costUploadResult ? 'Close' : 'Cancel'}
            </Button>
            {costUploadResult ? (
              <Button
                variant="secondary"
                onClick={() => {
                  setCostFiles([]);
                  setCostError(null);
                  setCostUploadResult(null);
                }}
              >
                <Upload className="w-4 h-4 mr-2" />Upload More
              </Button>
            ) : (
              <Button onClick={handleCostUpload} disabled={costFiles.length === 0 || costUploading}>
                {costUploading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Importing...</>
                ) : (
                  <><Upload className="w-4 h-4 mr-2" />Import {costFiles.length} File{costFiles.length !== 1 ? 's' : ''}</>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
